import api from '@/shared/utils/apiConfig';
import type { Patient, PatientRelationship } from '@/shared/types/patient';
import type { ImportCsvPreviewResult, ImportInspectResult } from '@/shared/import-export';
import {
  downloadBlob,
  filenameFromContentDisposition,
} from '@/shared/import-export';

interface PatientParams {
  search?: string;
  gender?: string;
  ageGroup?: string;
  clinic?: string;
  limit?: number;
  page?: number;
  [key: string]: any;
}

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  [key: string]: any;
}

export interface PatientListResponse extends ApiResponse<Patient[]> {
  total?: number;
  count?: number;
}

interface GetPatientsOptions {
  /**
   * When true (default, preserves prior behavior for existing callers), forces the
   * `X-Clinic-Scope: all` header so the request ignores the AppBar clinic scope.
   * Pass `{ allClinics: false }` for clinic-scoped list pages (e.g. Patients.tsx) so the
   * apiClient interceptor's default clinic-scope header (from localStorage) applies instead.
   */
  allClinics?: boolean;
  signal?: AbortSignal;
}

export const patientAPI = {
  getPatients: async (
    params: PatientParams = {},
    options: GetPatientsOptions = { allClinics: true }
  ): Promise<PatientListResponse> => {
    const response = await api.get('/patients', {
      params,
      headers: options.allClinics === false ? undefined : { 'X-Clinic-Scope': 'all' },
      signal: options.signal,
    });
    return response.data;
  },

  getPatient: async (id: string): Promise<ApiResponse<Patient>> => {
    const response = await api.get(`/patients/${id}`);
    return response.data;
  },

  createPatient: async (patientData: Record<string, unknown>): Promise<ApiResponse<Patient>> => {
    const response = await api.post('/patients', patientData);
    return response.data;
  },

  updatePatient: async (id: string, patientData: Record<string, unknown>): Promise<ApiResponse<Patient>> => {
    const response = await api.put(`/patients/${id}`, patientData);
    return response.data;
  },

  deletePatient: async (id: string): Promise<ApiResponse> => {
    const response = await api.delete(`/patients/${id}`);
    return response.data;
  },

  restorePatient: async (id: string): Promise<ApiResponse<Patient>> => {
    const response = await api.post(`/patients/${id}/restore`);
    return response.data;
  },

  purgePatient: async (
    id: string,
    body: { confirmPatientId: string }
  ): Promise<ApiResponse> => {
    const response = await api.post(`/patients/${id}/purge`, body);
    return response.data;
  },

  searchPatients: async (
    searchTerm: string,
    options: GetPatientsOptions = { allClinics: true }
  ): Promise<PatientListResponse> => {
    const response = await api.get('/patients', {
      params: { search: searchTerm, limit: 50 },
      // Match getPatients default: pickers search org-wide unless caller opts out.
      headers: options.allClinics === false ? undefined : { 'X-Clinic-Scope': 'all' },
      signal: options.signal,
    });
    return response.data;
  },

  addRelationship: async (
    patientId: string,
    payload: { targetId: string; relationshipType: string }
  ): Promise<ApiResponse<PatientRelationship[]>> => {
    const response = await api.post(`/patients/${patientId}/relationships`, payload);
    return response.data;
  },

  removeRelationship: async (
    patientId: string,
    targetId: string
  ): Promise<ApiResponse<PatientRelationship[]>> => {
    const response = await api.delete(`/patients/${patientId}/relationships/${targetId}`);
    return response.data;
  },

  exportCsv: async (): Promise<void> => {
    const response = await api.get('/patients/export', {
      responseType: 'blob',
    });
    const filename = filenameFromContentDisposition(
      response.headers['content-disposition'] as string | undefined,
      'patients.csv'
    );
    downloadBlob(response.data as Blob, filename);
  },

  downloadImportTemplate: async (): Promise<void> => {
    const response = await api.get('/patients/import/template', {
      responseType: 'blob',
    });
    const filename = filenameFromContentDisposition(
      response.headers['content-disposition'] as string | undefined,
      'patient-import-template.csv'
    );
    downloadBlob(response.data as Blob, filename);
  },

  inspectImport: async (
    file: File,
    onProgress?: (percent: number) => void
  ): Promise<ApiResponse<ImportInspectResult>> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/patients/import/inspect', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
      onUploadProgress: (event) => {
        if (!onProgress || !event.total) return;
        const percent = Math.min(100, Math.round((event.loaded / event.total) * 100));
        onProgress(percent);
      },
    });
    return response.data;
  },

  importCsv: async (
    file: File,
    dryRun: boolean,
    onProgress?: (percent: number) => void,
    columnMapping?: Record<string, string>
  ): Promise<ApiResponse<ImportCsvPreviewResult>> => {
    const formData = new FormData();
    formData.append('file', file);
    if (columnMapping && Object.keys(columnMapping).length > 0) {
      formData.append('columnMapping', JSON.stringify(columnMapping));
    }
    const response = await api.post('/patients/import', formData, {
      params: { dryRun: dryRun ? 'true' : 'false' },
      headers: { 'Content-Type': 'multipart/form-data' },
      // Large patient CSV (10k+ rows) needs headroom beyond dry-run; writes are bulk but still minutes-scale on slow DB.
      timeout: 600000,
      onUploadProgress: (event) => {
        if (!onProgress || !event.total) return;
        const percent = Math.min(100, Math.round((event.loaded / event.total) * 100));
        onProgress(percent);
      },
    });
    return response.data;
  },
};
